import { LDProvider, type LDContext, useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { type PropsWithChildren, useEffect, useState } from 'react';

import { useAuth } from '../hooks/useAuth';

const LD_ANONYMOUS_KEY_STORAGE_KEY = 'th.ld.anonymous.key';
const LD_SERVER_FALLBACK_KEY = 'therapyhelps-web-anonymous';
const launchDarklyClientSideID = process.env.NEXT_PUBLIC_LD_CLIENT_SIDE_ID?.trim();

export const featureFlagKeys = {
  simplifiedCampaignHeader: 'simplified-campaign-header',
} as const;

function createAnonymousKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `anon-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

function getLaunchDarklyContext(): LDContext {
  if (typeof window === 'undefined') {
    return { kind: 'user', key: LD_SERVER_FALLBACK_KEY, anonymous: true };
  }

  const storedKey = window.localStorage.getItem(LD_ANONYMOUS_KEY_STORAGE_KEY);
  if (storedKey) {
    return { kind: 'user', key: storedKey, anonymous: true };
  }

  const nextKey = createAnonymousKey();
  window.localStorage.setItem(LD_ANONYMOUS_KEY_STORAGE_KEY, nextKey);
  return { kind: 'user', key: nextKey, anonymous: true };
}

function LaunchDarklyIdentity() {
  const { user } = useAuth();
  const ldClient = useLDClient();

  useEffect(() => {
    if (!ldClient) return;

    const context: LDContext = user
      ? {
          kind: 'user',
          key: user.id,
          name: user.name,
          email: user.email,
          role: user.role,
        }
      : getLaunchDarklyContext();

    void ldClient.identify(context).catch(() => {
      // Keep the last evaluated flags if LaunchDarkly is temporarily unavailable.
    });
  }, [ldClient, user?.email, user?.id, user?.name, user?.role]);

  return null;
}

export function FeatureFlagsProvider({ children }: PropsWithChildren) {
  const [context] = useState<LDContext>(() => getLaunchDarklyContext());

  if (!launchDarklyClientSideID) {
    return <>{children}</>;
  }

  return (
    <LDProvider
      clientSideID={launchDarklyClientSideID}
      context={context}
      // The storefront uses LaunchDarkly only for feature flags. Disable event
      // telemetry so the SDK does not continually POST analytics batches.
      options={{ sendEvents: false }}
      reactOptions={{ useCamelCaseFlagKeys: false }}
    >
      <LaunchDarklyIdentity />
      {children as any}
    </LDProvider>
  );
}

/**
 * This remains off until a LaunchDarkly experiment explicitly enables it.
 * It prevents navigation removal from affecting organic and returning users.
 */
export function useSimplifiedCampaignHeaderEnabled() {
  const flags = useFlags<Record<string, unknown>>();

  return flags[featureFlagKeys.simplifiedCampaignHeader] === true;
}
